import { useMemo } from 'react'
import useSWR from 'swr'
import { asPipelineId, type PipelineId } from '@ludus/core'
import { fetchPipelineManagers, fetchPipelineStatuses, listPipelines } from '@/lib/api/pipelines.ts'
import { fetchMailTemplates, type MailTemplate } from '@/lib/api/mail.ts'
import type { NodeField } from '@/features/flows/types.ts'

/**
 * The choices a `pipeline` / `status` / `manager` field offers.
 *
 * **Which board to ask is read off the field, never inferred.** `NodeField
 * .scopedBy` names the option holding the pipeline id — `targetStatusId` is
 * scoped by `targetPipelineId` — and its absence means the flow's own board.
 * That indirection is the whole reason the console can generate four drawers
 * without knowing what any node type's options mean.
 *
 * The keys are the URL strings the rest of the console uses
 * (`/pipelines/:id/statuses`), so the pipeline settings page and this drawer
 * share one cache entry rather than fetching the same board twice.
 */

export interface StatusChoice {
  id: string
  name: string
  /**
   * A flow-created card may never land on an `awaiting_distribution` status
   * (D6) — the runtime refuses one. The drawer shows such a status **disabled
   * with the reason** rather than hiding it, because "why is my status
   * missing?" is the question hiding it would create.
   */
  isDistributionTrigger: boolean
}

export interface ManagerChoice {
  id: string
  name: string
}

export interface PipelineChoice {
  id: string
  name: string
}

/**
 * The mails this instance's sink offers (#377).
 *
 * Board-independent, unlike every other choice hook here: a mail is a fact
 * about the sink, not about a pipeline, so there is nothing to scope it by.
 *
 * `configured: false` is a first-class answer and the drawer renders it as a
 * sentence rather than as an empty picker — «this instance sends no mail» is
 * something an operator can act on, and a silent empty list is not.
 */
export function useMailTemplateChoices(enabled: boolean): {
  choices: MailTemplate[]
  configured: boolean
  isLoading: boolean
  failed: boolean
} {
  const swr = useSWR(enabled ? '/mail/templates' : null, fetchMailTemplates)
  return {
    choices: swr.data?.items ?? [],
    // Optimistic while loading: assuming «not configured» would flash the
    // no-sink sentence on every drawer open before the answer arrives.
    configured: swr.data?.configured ?? true,
    isLoading: swr.isLoading,
    failed: swr.error !== undefined,
  }
}

export function usePipelineChoices(): { choices: PipelineChoice[]; isLoading: boolean } {
  const swr = useSWR('/pipelines', listPipelines)
  const choices = useMemo(
    () => (swr.data ?? []).map((p) => ({ id: String(p.id), name: p.name })),
    [swr.data],
  )
  return { choices, isLoading: swr.isLoading }
}

/**
 * The board a field's choices come from: its `scopedBy` option, else the flow's
 * own pipeline. `null` when the scoping option has not been answered yet, which
 * is what makes the status picker wait for a target pipeline rather than
 * offering the source board's statuses by mistake.
 */
export function boardForField(
  field: NodeField,
  options: Record<string, unknown>,
  ownPipelineId: PipelineId,
): PipelineId | null {
  if (field.scopedBy === undefined) return ownPipelineId
  const scoped = options[field.scopedBy]
  return typeof scoped === 'string' && scoped !== '' ? asPipelineId(scoped) : null
}

export function useStatusChoices(board: PipelineId | null): {
  choices: StatusChoice[]
  isLoading: boolean
} {
  const swr = useSWR(board === null ? null : `/pipelines/${board}/statuses`, () =>
    fetchPipelineStatuses(board as PipelineId),
  )
  const choices = useMemo(
    () =>
      (swr.data ?? []).map((s) => ({
        id: String(s.id),
        name: s.name,
        isDistributionTrigger: s.engineRole === 'awaiting_distribution',
      })),
    [swr.data],
  )
  return { choices, isLoading: swr.isLoading }
}

export function useManagerChoices(board: PipelineId | null): {
  choices: ManagerChoice[]
  isLoading: boolean
} {
  const swr = useSWR(board === null ? null : `/pipelines/${board}/managers`, () =>
    fetchPipelineManagers(board as PipelineId),
  )
  const choices = useMemo(
    () =>
      (swr.data ?? []).map((m) => ({
        id: String(m.id),
        name: `${m.firstName} ${m.lastName}`.trim(),
      })),
    [swr.data],
  )
  return { choices, isLoading: swr.isLoading }
}
